// schema.js
// ProseMirror schema with support for block IDs, author tracking, and meta marks

import {Schema} from "prosemirror-model";
import {schema as basicSchema} from "prosemirror-schema-basic";
import {addListNodes} from "prosemirror-schema-list";
import {tableNodes} from "prosemirror-tables";
import {addSuggestionMarks} from "@handlewithcare/prosemirror-suggest-changes";
import {createListSpec, createParseDomRules, flatListGroup} from "prosemirror-flat-list";
import {generateUUID} from "../helpers/utils";
import {agenticSectionNode, hashtagNode, linkEmbedNode, mapNode, userMentionNode} from "./nodes";
import {isLinkedEntityRef} from "../../../utils/entityLinks";

/**
 * Parse a confidence value from a DOM data attribute into a finite number.
 * Returns null when absent/invalid so the mention hover popup can gracefully
 * omit confidence for older documents.
 *
 * Note: values are returned as-is. If the source stored a percentage-scale
 * integer (0–100) the caller is responsible for normalising to [0,1].
 */
function parseConfidenceAttr(raw) {
    if (raw == null || raw === "") return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    return n;
}

/**
 * Safely parse a JSON string that should decode to an array.
 * Returns an empty array when the string is absent, null, or malformed.
 */
const safeParseArray = (raw) => {
    try { return JSON.parse(raw ?? "[]"); } catch { return []; }
};

/**
 * Extract Wikidata mark attributes from a DOM element.
 * Shared by both parseDOM rules on wikidataEntity (linked spans and
 * unlinked mention-only spans) so each rule stays a one-liner.
 */
function parseWikidataAttrs(dom) {
    const nerScore = parseConfidenceAttr(dom.getAttribute("data-ner-score"))
        ?? parseConfidenceAttr(dom.getAttribute("data-confidence"));
    return {
        id: dom.getAttribute("data-wikidata-id"),
        uid: dom.getAttribute("data-wikidata-uid") || `wde-${generateUUID()}`,
        label: dom.getAttribute("data-wikidata-label"),
        url: dom.getAttribute("data-wikidata-url"),
        description: dom.getAttribute("data-wikidata-description"),
        imageUrl: dom.getAttribute("data-wikidata-image-url"),
        entityType: dom.getAttribute("data-entity-type"),
        confidence: nerScore,
        nerScore,
        linkingScore: parseConfidenceAttr(dom.getAttribute("data-linking-score")),
    };
}

/**
 * Extract `citationRef` mark attributes from a DOM element's `data-citation-ref-*`
 * attributes. Shared by the mark's own `parseDOM` rule (below) AND
 * `citationRefPopoverPlugin.jsx`'s live DOM-event reads, so the two never drift apart —
 * mirrors `parseWikidataAttrs` above.
 */
export function parseCitationRefAttrs(dom) {
    const numberAttr = dom.getAttribute("data-citation-ref-number");
    const referenceIdAttr = dom.getAttribute("data-citation-ref-reference-id");
    const canonicalFileIdAttr = dom.getAttribute("data-citation-ref-canonical-file-id");
    const docIdAttr = dom.getAttribute("data-citation-ref-doc-id");
    const segmentIndexAttr = dom.getAttribute("data-citation-ref-segment-index");
    let sentenceHit = null;
    try {
        const raw = dom.getAttribute("data-citation-ref-sentence-hit");
        sentenceHit = raw ? JSON.parse(raw) : null;
    } catch {
        sentenceHit = null;
    }
    return {
        key: dom.getAttribute("data-citation-ref-key"),
        number: numberAttr != null && numberAttr !== "" ? Number(numberAttr) : null,
        kind: dom.getAttribute("data-citation-ref-kind"),
        title: dom.getAttribute("data-citation-ref-title"),
        snippet: dom.getAttribute("data-citation-ref-snippet"),
        documentType: dom.getAttribute("data-citation-ref-document-type"),
        referenceId: referenceIdAttr != null && referenceIdAttr !== "" ? Number(referenceIdAttr) : null,
        canonicalFileId: canonicalFileIdAttr != null && canonicalFileIdAttr !== "" ? Number(canonicalFileIdAttr) : null,
        docId: docIdAttr != null && docIdAttr !== "" ? Number(docIdAttr) : null,
        segmentIndex: segmentIndexAttr != null && segmentIndexAttr !== "" ? Number(segmentIndexAttr) : null,
        sentenceHit,
    };
}

/**
 * Add blockId and classificationLevel attributes to all block-level nodes.
 * This enables block-level tracking, references, and classification.
 */
function addBlockIdToNodes(nodes) {
    const result = {};

    nodes.forEach((name, spec) => {
        if (!spec) {
            result[name] = spec;
            return;
        }

        // Use a word-boundary regex so compound group strings (e.g. "block flat-list")
        // are matched correctly regardless of whitespace variation.
        const isBlock = spec.group != null && /\bblock\b/.test(spec.group);
        if (!isBlock) {
            result[name] = spec;
            return;
        }

        const attrs = {
            ...(spec.attrs || {}),
            blockId: {default: null},
        };

        // Pre-compute once per spec; used in both toDOM and parseDOM closures.
        const isClassified = name === 'paragraph' || name === 'heading';

        // Add classification level attributes to paragraph and heading nodes
        if (isClassified) {
            attrs.classificationLevel = {default: 'U'};
            // The level the user last explicitly chose via the Classification menu — distinct
            // from `classificationLevel` (the effective, possibly citation-raised level) so a
            // manual choice survives a reload even after a citation temporarily raises the
            // effective level and is later removed. `null` means "never manually touched" —
            // referenceClassificationFloor.js reads this attr directly as the floor's baseline.
            attrs.manualClassificationLevel = {default: null};
            attrs.distribution = {default: []};
            attrs.compartment = {default: []};
            attrs.aiProcessing = {default: {}};
        }

        // Preserve the id and classification level in HTML as data attributes when possible
        const toDOM = spec.toDOM
            ? (node) => {
                const dom = spec.toDOM(node);
                if (!Array.isArray(dom)) {
                    return dom;
                }

                // Build attributes object
                const dataAttrs = {
                    "data-block-id": node.attrs.blockId || null
                };

                // Add classification level for paragraph and heading nodes
                if (isClassified) {
                    const classLevel = node.attrs.classificationLevel || 'U';
                    dataAttrs["data-classification-level"] = classLevel;
                    if (node.attrs.manualClassificationLevel) {
                        dataAttrs["data-manual-classification-level"] = node.attrs.manualClassificationLevel;
                    }
                    // Serialize distribution and compartment arrays as JSON strings
                    if (node.attrs.distribution && node.attrs.distribution.length > 0) {
                        dataAttrs["data-distribution"] = JSON.stringify(node.attrs.distribution);
                    }
                    if (node.attrs.compartment && node.attrs.compartment.length > 0) {
                        dataAttrs["data-compartment"] = JSON.stringify(node.attrs.compartment);
                    }
                    if (node.attrs.aiProcessing && Object.keys(node.attrs.aiProcessing).length > 0) {
                        dataAttrs["data-ai-processing"] = JSON.stringify(node.attrs.aiProcessing);
                    }
                }

                // Check if second element is an attributes object
                if (
                    dom[1] &&
                    typeof dom[1] === "object" &&
                    !Array.isArray(dom[1]) &&
                    !(dom[1] instanceof Node)
                ) {
                    // Merge our data attributes with existing attributes (non-mutating)
                    return [dom[0], {...dom[1], ...dataAttrs}, ...dom.slice(2)];
                } else {
                    // Construct a new array with the attrs object inserted at position 1;
                    // avoids mutating the array returned by spec.toDOM which may be a
                    // shared/cached reference (e.g. a module-level constant like ["hr"]).
                    return [dom[0], dataAttrs, ...dom.slice(1)];
                }
            }
            : spec.toDOM;

        const parseDOM = Array.isArray(spec.parseDOM)
            ? spec.parseDOM.map((rule) => {
                const getAttrs = rule.getAttrs;
                return {
                    ...rule,
                    getAttrs: (dom) => {
                        const baseAttrs =
                            typeof getAttrs === "function" ? getAttrs(dom) : getAttrs || {};
                        if (baseAttrs === false) return false;

                        // ALWAYS generate blockId during parsing if not present.
                        // This handles paste, load from HTML, etc.
                        const dataBlockId = dom?.getAttribute?.("data-block-id");
                        const attrs = {
                            ...(baseAttrs || {}),
                            blockId: dataBlockId || generateUUID(),
                        };

                        // Parse classification level for paragraph and heading nodes
                        if (isClassified) {
                            const dataClassificationLevel = dom?.getAttribute?.("data-classification-level");
                            attrs.classificationLevel = dataClassificationLevel || (baseAttrs && baseAttrs.classificationLevel) || null;

                            const dataManualClassificationLevel = dom?.getAttribute?.("data-manual-classification-level");
                            attrs.manualClassificationLevel = dataManualClassificationLevel
                                || (baseAttrs && baseAttrs.manualClassificationLevel)
                                || null;

                            // Parse distribution array
                            const dataDistribution = dom?.getAttribute?.("data-distribution");
                            attrs.distribution = dataDistribution
                                ? safeParseArray(dataDistribution)
                                : (baseAttrs && baseAttrs.distribution) || [];

                            // Parse compartment array
                            const dataCompartment = dom?.getAttribute?.("data-compartment");
                            attrs.compartment = dataCompartment
                                ? safeParseArray(dataCompartment)
                                : (baseAttrs && baseAttrs.compartment) || [];

                            // Parse AI processing status map
                            const dataAiProcessing = dom?.getAttribute?.("data-ai-processing");
                            try {
                                attrs.aiProcessing = dataAiProcessing
                                    ? JSON.parse(dataAiProcessing)
                                    : (baseAttrs && baseAttrs.aiProcessing) || {};
                            } catch {
                                attrs.aiProcessing = {};
                            }
                        }
                        return attrs;
                    },
                };
            })
            : spec.parseDOM;

        result[name] = {
            ...spec,
            attrs,
            toDOM,
            parseDOM,
        };
    });

    return result;
}

/**
 * Create the editor schema with all necessary marks and nodes
 */
export const editorSchema = new Schema({
    nodes: addBlockIdToNodes(
        // Add legacy list nodes (bullet_list, ordered_list, list_item) for backward
        // compatibility with existing Yjs documents that contain these node types.
        // New lists are created using the flat-list `list` node.
        addListNodes(basicSchema.spec.nodes, "paragraph block*", "block")
            // Flat list node — single node type replacing bullet/ordered/task lists
            // going forward. Parses legacy <ul>/<ol> HTML as well via createParseDomRules().
            .append({
                list: {
                    ...createListSpec(),
                    group: `block ${flatListGroup}`,
                    parseDOM: [...createParseDomRules()],
                },
            })
            // Add table nodes (table, table_row, table_cell, table_header)
            .append(tableNodes({
                tableGroup: "block",
                cellContent: "block+",
                cellAttributes: {
                    background: {
                        default: null,
                        getFromDOM(dom) {
                            return dom.style.backgroundColor || null
                        },
                        setDOMAttr(value, attrs) {
                            if (value) attrs.style = (attrs.style || "") + `background-color: ${value};`
                        }
                    }
                }
            }))
            // Add heading nodes (h1-h6)
            .update("heading", {
                attrs: {level: {default: 1}},
                content: "inline*",
                group: "block",
                defining: true,
                parseDOM: [
                    {tag: "h1", attrs: {level: 1}},
                    {tag: "h2", attrs: {level: 2}},
                    {tag: "h3", attrs: {level: 3}},
                    {tag: "h4", attrs: {level: 4}},
                    {tag: "h5", attrs: {level: 5}},
                    {tag: "h6", attrs: {level: 6}}
                ],
                toDOM(node) {
                    return ["h" + node.attrs.level, 0]
                }
            })
            // Horizontal rule: override the basicSchema spec to use a minimal definition.
            // addBlockIdToNodes injects blockId automatically for all block nodes.
            .update("horizontal_rule", {
                group: "block",
                parseDOM: [{tag: "hr"}],
                toDOM() {
                    return ["hr"]
                }
            })
            // figure — single atom block node representing an image/video/audio with a caption.
            // All media and caption data live in attrs so the node is a true atom:
            // deleting it removes everything at once, and paste/schema-parse never
            // produces stray child nodes.
            .append({
                figure: {
                    group: "block",
                    atom: true,
                    selectable: true, // atom nodes default to true; made explicit for clarity
                    draggable: false,
                    attrs: {
                        // Media
                        src: {default: ''},
                        alt: {default: null},
                        title: {default: null},
                        width: {default: null},
                        height: {default: null},
                        align: {default: 'center'},
                        uploading: {default: false},
                        mediaType: {default: 'image'}, // 'image' | 'audio' | 'video'
                        // Caption text stored as plain string attr
                        caption: {default: ''},
                        // Classification (same set as paragraph)
                        classificationLevel: {default: 'U'},
                        distribution: {default: []},
                        compartment: {default: []},
                        // blockId is injected by addBlockIdToNodes for all block nodes;
                        // do not declare it here to avoid a redundant duplicate definition.
                    },
                    parseDOM: [
                        {
                            tag: "figure",
                            getAttrs(dom) {
                                const img   = dom.querySelector('img');
                                const video = dom.querySelector('video');
                                const audio = dom.querySelector('audio');
                                const cap   = dom.querySelector('figcaption');
                                let src = '', alt = null, title = null, width = null, height = null,
                                    align = 'center', mediaType = 'image';
                                if (img) {
                                    src = img.getAttribute('src') || '';
                                    alt = img.getAttribute('alt');
                                    title = img.getAttribute('title');
                                    width = img.getAttribute('width');
                                    height = img.getAttribute('height');
                                    align = img.getAttribute('data-align') || 'center';
                                    mediaType = 'image';
                                } else if (video) {
                                    src = video.getAttribute('src') || '';
                                    title = video.getAttribute('title');
                                    width = video.getAttribute('width');
                                    height = video.getAttribute('height');
                                    align = video.getAttribute('data-align') || 'center';
                                    mediaType = 'video';
                                } else if (audio) {
                                    src = audio.getAttribute('src') || '';
                                    title = audio.getAttribute('title');
                                    mediaType = 'audio';
                                }
                                return {
                                    src, alt, title, width, height, align, mediaType,
                                    caption: cap ? cap.textContent || '' : '',
                                    classificationLevel: dom.getAttribute("data-classification-level") || 'U',
                                    distribution: safeParseArray(dom.getAttribute("data-distribution")),
                                    compartment: safeParseArray(dom.getAttribute("data-compartment")),
                                };
                            },
                        },
                    ],
                    toDOM(node) {
                        const {src, alt, title, width, height, align, uploading, mediaType,
                               caption, classificationLevel, distribution, compartment, blockId} = node.attrs;

                        const figAttrs = {
                            'data-classification-level': classificationLevel || 'U',
                        };
                        if (blockId) figAttrs['data-block-id'] = blockId;
                        if (distribution && distribution.length > 0) {
                            figAttrs['data-distribution'] = JSON.stringify(distribution);
                        }
                        if (compartment && compartment.length > 0) {
                            figAttrs['data-compartment'] = JSON.stringify(compartment);
                        }

                        let mediaEl;
                        if (mediaType === 'audio') {
                            mediaEl = ['audio', {src, controls: true, ...(title ? {title} : {})}];
                        } else if (mediaType === 'video') {
                            const va = {src, controls: true};
                            if (title) va.title = title;
                            if (width) va.width = width;
                            if (height) va.height = height;
                            if (align) va['data-align'] = align;
                            mediaEl = ['video', va];
                        } else {
                            const ia = {src, alt: alt || ''};
                            if (title) ia.title = title;
                            if (width) ia.width = width;
                            if (height) ia.height = height;
                            if (align) ia['data-align'] = align;
                            if (uploading) ia.class = 'image-uploading';
                            mediaEl = ['img', ia];
                        }

                        return ["figure", figAttrs,
                            ["div", {class: "pm-figure-media-wrapper"}, mediaEl],
                            ["figcaption", {class: "pm-figure-caption"}, caption || ''],
                        ];
                    },
                },
            })
            // Add userMention inline node (imported from separate file)
            .append({
                userMention: userMentionNode
            })
            // Add hashtag inline node (imported from separate file)
            .append({
                hashtag: hashtagNode
            })
            // Add link embed block node
            .append({
                link_embed: linkEmbedNode
            })
            // Add agentic section block node
            .append({
                agenticSection: agenticSectionNode
            })
            // Add interactive map block node
            .append({
                map: mapNode
            })
    ),
    // Mark ordering controls DOM nesting: ProseMirror renders earlier-defined marks
    // as OUTER wrappers in the serialised HTML. To achieve the desired nesting:
    //
    //   meta marks (comment / reference / task / wikidataEntity)  ← outermost
    //     → author
    //       → strong / em / code
    //         → link
    //           → suggestion marks                                 ← innermost
    //
    // The built-in link, strong, em, and code marks are removed from basicSchema's
    // OrderedMap and re-appended at the end so they rank lower than the meta marks.
    // This re-append is intentional — never reorder without understanding this contract.
    marks: basicSchema.spec.marks
        .remove("link")
        .remove("strong")
        .remove("em")
        .remove("code")
        .append({
            // Meta marks FIRST — they must be the outermost wrappers so their
            // highlight / active styling covers all inner formatting & links.
            comment: {
                attrs: {
                    uid: {}
                },
                inclusive: false,
                excludes: "", // Allow overlapping
                parseDOM: [{
                    tag: "span[data-comment-uid]",
                    getAttrs(dom) {
                        return {uid: dom.getAttribute("data-comment-uid")};
                    }
                }],
                toDOM(mark) {
                    return ["span", {
                        "data-comment-uid": mark.attrs.uid,
                        "class": "meta-mark meta-comment"
                    }, 0];
                }
            },
            reference: {
                attrs: {
                    uid: {}
                },
                inclusive: false, // must not extend to newly typed text at mark boundaries
                excludes: "",
                parseDOM: [{
                    tag: "span[data-reference-uid]",
                    getAttrs(dom) {
                        return {uid: dom.getAttribute("data-reference-uid")};
                    }
                }],
                toDOM(mark) {
                    return ["span", {
                        "data-reference-uid": mark.attrs.uid,
                        "class": "meta-mark meta-reference"
                    }, 0];
                }
            },
            task: {
                attrs: {
                    uid: {}
                },
                inclusive: false,
                excludes: "",
                parseDOM: [{
                    tag: "span[data-task-uid]",
                    getAttrs(dom) {
                        return {uid: dom.getAttribute("data-task-uid")};
                    }
                }],
                toDOM(mark) {
                    return ["span", {
                        "data-task-uid": mark.attrs.uid,
                        "class": "meta-mark meta-task"
                    }, 0];
                }
            },
            // Wikidata entity mark
            wikidataEntity: {
                attrs: {
                    id: {default: null},
                    uid: {default: null},
                    label: {default: null},
                    url: {default: null},
                    description: {default: null},
                    imageUrl: {default: null},
                    entityType: {default: null},
                    // Legacy single score (mirrors nerScore). Kept for back-compat with
                    // documents/data attributes produced before the dual-score split.
                    confidence: {default: null},
                    // Mention-detection confidence (how sure the model is this span is an entity).
                    nerScore: {default: null},
                    // Entity-linking confidence (how sure the model is about the linked QID).
                    linkingScore: {default: null},
                    // Provenance marker (e.g. manual-annotation) — no DB column; stored in the doc mark.
                    source: {default: null},
                    // Display name of the user who manually linked this span.
                    linkedBy: {default: null},
                },
                inclusive: false,
                excludes: "",
                parseDOM: [
                    {
                        tag: "span[data-wikidata-id]",
                        getAttrs: parseWikidataAttrs,
                    },
                    {
                        // Unlinked mention spans carry no QID — match the modifier class instead.
                        tag: "span.wikidata-entity--mention",
                        getAttrs: parseWikidataAttrs,
                    },
                ],
                toDOM(mark) {
                    const id = mark.attrs.id;
                    const uid = mark.attrs.uid;
                    // A Wikidata QID (Qxxx) or custom D ref means the span is *linked* → blue styling.
                    // A span without a linked ref is a recognized *mention* → yellow underline only.
                    const isLinked = isLinkedEntityRef(id);
                    const attrs = {
                        "class": isLinked
                            ? "wikidata-entity wikidata-entity--linked"
                            : "wikidata-entity wikidata-entity--mention"
                    };
                    if (isLinked) attrs["data-wikidata-id"] = id;
                    if (uid) attrs["data-wikidata-uid"] = uid;
                    else if (isLinked) attrs["data-wikidata-uid"] = id;
                    if (mark.attrs.label) attrs["data-wikidata-label"] = mark.attrs.label;
                    if (mark.attrs.url) attrs["data-wikidata-url"] = mark.attrs.url;
                    if (mark.attrs.description) attrs["data-wikidata-description"] = mark.attrs.description;
                    if (mark.attrs.imageUrl) attrs["data-wikidata-image-url"] = mark.attrs.imageUrl;
                    if (mark.attrs.entityType) attrs["data-entity-type"] = mark.attrs.entityType;
                    // nerScore is the canonical mention score; fall back to legacy confidence.
                    const nerScore = mark.attrs.nerScore != null && mark.attrs.nerScore !== ""
                        ? mark.attrs.nerScore
                        : mark.attrs.confidence;
                    if (nerScore != null && nerScore !== "") {
                        attrs["data-ner-score"] = String(nerScore);
                        // Only preserve data-confidence when it carries distinct information from
                        // nerScore (i.e. a separately-specified legacy confidence value differs).
                        // When they match (or confidence was omitted) there is no new information,
                        // so omit the redundant attribute to keep the DOM clean.
                        const legacyConfidence = mark.attrs.confidence;
                        if (legacyConfidence != null && legacyConfidence !== "" && legacyConfidence !== nerScore) {
                            attrs["data-confidence"] = String(legacyConfidence);
                        }
                    }
                    if (mark.attrs.linkingScore != null && mark.attrs.linkingScore !== "") {
                        attrs["data-linking-score"] = String(mark.attrs.linkingScore);
                    }
                    if (mark.attrs.source) attrs["data-source"] = mark.attrs.source;
                    if (mark.attrs.linkedBy) attrs["data-linked-by"] = mark.attrs.linkedBy;

                    return ["span", attrs, 0];
                }
            },
            // MINSKY-16823 (ticket 04): forecast citation mark — a new, distinct mark type,
            // NOT a reuse of the `reference` mark above. `reference` is coupled to the Yjs
            // `Y.Map("referenceMarks")` and margin-rail citation system; this mark instead
            // carries its own source metadata directly in its attrs (JSON-serializable, so it
            // round-trips through Yjs/HTML like `distribution`/`aiProcessing` above) so the
            // in-document popover (citationRefPopoverPlugin.jsx) can render/open-source without
            // any lookup into a separate store. Numbering is fixed at insertion time by the
            // caller (Forecast.jsx) — it does not get recomputed from document position.
            citationRef: {
                attrs: {
                    key: {default: null},
                    number: {default: null},
                    kind: {default: null},
                    title: {default: null},
                    snippet: {default: null},
                    documentType: {default: null},
                    referenceId: {default: null},
                    canonicalFileId: {default: null},
                    // MINSKY-16821 gap fix: corpus-source identifiers, mirroring the
                    // data-item identifiers above — needed so an in-document `corpus` mark can
                    // open `useSourcePackageBuilderStore`'s corpus view the same way the
                    // Forecast panel preview's popover does.
                    docId: {default: null},
                    segmentIndex: {default: null},
                    sentenceHit: {default: null},
                },
                inclusive: false,
                excludes: "",
                parseDOM: [{
                    tag: "sup[data-citation-ref-key]",
                    getAttrs: parseCitationRefAttrs
                }],
                toDOM(mark) {
                    const attrs = {
                        "data-citation-ref-key": mark.attrs.key || "",
                        "class": "citation-ref-mark",
                    };
                    if (mark.attrs.number != null) attrs["data-citation-ref-number"] = String(mark.attrs.number);
                    if (mark.attrs.kind) attrs["data-citation-ref-kind"] = mark.attrs.kind;
                    if (mark.attrs.title) attrs["data-citation-ref-title"] = mark.attrs.title;
                    if (mark.attrs.snippet) attrs["data-citation-ref-snippet"] = mark.attrs.snippet;
                    if (mark.attrs.documentType) attrs["data-citation-ref-document-type"] = mark.attrs.documentType;
                    if (mark.attrs.referenceId != null) attrs["data-citation-ref-reference-id"] = String(mark.attrs.referenceId);
                    if (mark.attrs.canonicalFileId != null) attrs["data-citation-ref-canonical-file-id"] = String(mark.attrs.canonicalFileId);
                    if (mark.attrs.docId != null) attrs["data-citation-ref-doc-id"] = String(mark.attrs.docId);
                    if (mark.attrs.segmentIndex != null) attrs["data-citation-ref-segment-index"] = String(mark.attrs.segmentIndex);
                    if (mark.attrs.sentenceHit) attrs["data-citation-ref-sentence-hit"] = JSON.stringify(mark.attrs.sentenceHit);
                    return ["sup", attrs, 0];
                }
            },
            // Author mark - tracks who wrote what.
            // parseDOM restores authorship from stored HTML and clipboard round-trips.
            // If clipboard-paste attribution should be stripped in the future, add a
            // `context` filter to the parse rule rather than removing it entirely.
            author: {
                attrs: {
                    userId: {}
                },
                inclusive: false, // critical - don't extend to new content
                excludes: "", // IMPORTANT: allow overlapping with other marks
                parseDOM: [{
                    tag: "span[data-author]",
                    getAttrs(dom) {
                        return {userId: dom.getAttribute("data-author")};
                    }
                }],
                toDOM(mark) {
                    return ["span", {"data-author": mark.attrs.userId}, 0]
                }
            },
            // Re-added formatting marks (originally from basicSchema) at a lower
            // rank so they nest inside meta marks and author.
            strong: {
                parseDOM: [
                    {tag: "strong"},
                    {tag: "b", getAttrs: node => node.style.fontWeight !== "normal" && null},
                    {style: "font-weight=400", clearMark: m => m.type.name === "strong"},
                    {style: "font-weight", getAttrs: value => /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null},
                ],
                toDOM() {
                    return ["strong", 0]
                },
            },
            em: {
                parseDOM: [
                    {tag: "i"},
                    {tag: "em"},
                    {style: "font-style=italic"},
                    {style: "font-style=normal", clearMark: m => m.type.name === "em"},
                ],
                toDOM() {
                    return ["em", 0]
                },
            },
            code: {
                parseDOM: [{tag: "code"}],
                toDOM() {
                    return ["code", 0]
                },
            },
            // Link mark is defined LAST so it has the lowest rank (inner-most wrapper).
            // This ensures reference/comment spans always wrap around <a> tags in the DOM,
            // which is what the application expects when rendering reference highlights.
            link: {
                attrs: {
                    href: {},
                    title: {default: null},
                },
                inclusive: false,
                excludes: "", // allow combining with meta marks
                parseDOM: [
                    {
                        tag: "a[href]",
                        getAttrs(dom) {
                            return {
                                href: dom.getAttribute("href"),
                                title: dom.getAttribute("title"),
                            };
                        },
                    },
                ],
                toDOM(mark) {
                    const {href, title} = mark.attrs;
                    const attrs = {href};
                    if (title) attrs.title = title;
                    return ["a", attrs, 0];
                },
            },
            // Track-changes suggestion marks (added by prosemirror-suggestion-mode).
            // Defined last so they are the innermost wrappers and do not disrupt
            // the meta-mark → author → formatting → link nesting order.
            ...addSuggestionMarks({}),
        }),
});

/**
 * Backward-compatible schema factory for callers/tests that expect a function.
 */
export function createEditorSchema() {
    return editorSchema;
}
